/* Caveau - barra di ricerca sotto al logo (sections/caveau-header-search.liquid).
   Mentre si scrive chiede a Shopify la ricerca predittiva disegnata da sections/caveau-predictive.liquid
   e la mostra a tendina: brand e collezioni, profumi con foto e prezzo, "Vedi tutti i risultati".
   Tastiera: frecce su/giu, Invio, Esc. La lente dell'header porta a questa barra.
   Telefono: la tendina arriva fino al bordo visibile dello schermo, sopra la tastiera (window.visualViewport), e si scorre
   con il dito; appena si trascina la lista la tastiera si chiude e la tendina resta aperta, più alta. */
(() => {
  const root = document.querySelector('[data-cv-hfind]');
  if (!root || root.dataset.cvReady) return;
  root.dataset.cvReady = '1';

  const input = root.querySelector('[data-cv-hfind-input]');
  const panel = root.querySelector('[data-cv-hfind-panel]');
  const results = root.querySelector('[data-cv-hfind-results]');
  const popular = root.querySelector('[data-cv-hfind-popular]');
  const clearBtn = root.querySelector('[data-cv-hfind-clear]');
  const spinner = root.querySelector('[data-cv-hfind-spinner]');
  if (!input || !panel || !results) return;

  const suggestUrl = root.dataset.suggestUrl || '/search/suggest';
  const resultsUrl = root.dataset.resultsUrl || '/search';
  const sectionId = root.dataset.sectionId || 'caveau-predictive';
  const cache = new Map();
  let controller = null;
  let timer = null;
  let activeIndex = -1;
  let optionSeq = 0;
  const vv = window.visualViewport || null;
  let keepOpen = false;
  let touchStartY = null;
  let fitFrame = 0;

  /* Spazio per la tendina: dal suo bordo alto al fondo della parte visibile dello schermo (sopra la tastiera). */
  const fitPanel = () => {
    fitFrame = 0;
    if (panel.hidden) return;
    const top = panel.getBoundingClientRect().top;
    const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const space = Math.max(Math.floor(bottom - top - 12), 160);
    root.style.setProperty('--cv-hfind-space', space + 'px');
  };
  const scheduleFit = () => {
    if (!fitFrame) fitFrame = window.requestAnimationFrame(fitPanel);
  };

  const term = () => input.value.trim();
  const visibleOptions = () => [...panel.querySelectorAll('[role="option"]')].filter((el) => el.offsetParent !== null);

  const setActive = (index) => {
    const list = visibleOptions();
    list.forEach((el) => el.classList.remove('is-active'));
    if (index < 0 || index >= list.length) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }
    activeIndex = index;
    const el = list[index];
    if (!el.id) {
      optionSeq += 1;
      el.id = 'cv-hfind-opt-' + optionSeq;
    }
    el.classList.add('is-active');
    input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest' });
  };

  const open = () => {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    root.classList.add('is-open');
    fitPanel();
  };

  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
    setActive(-1);
  };

  const showPopular = () => {
    if (!popular) {
      close();
      return;
    }
    results.replaceChildren();
    popular.hidden = false;
    setActive(-1);
    open();
  };

  const goToResults = (q) => {
    if (!q) return;
    window.location.href = resultsUrl + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
  };

  const highlight = (q) => {
    const needle = q.toLowerCase();
    if (needle.length < 2) return;
    results.querySelectorAll('[data-cv-hl]').forEach((el) => {
      const text = el.textContent;
      const at = text.toLowerCase().indexOf(needle);
      if (at < 0) return;
      const mark = document.createElement('mark');
      mark.textContent = text.slice(at, at + needle.length);
      el.textContent = '';
      el.append(text.slice(0, at), mark, text.slice(at + needle.length));
    });
  };

  const render = (q, html) => {
    if (q !== term()) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('.cv-hs');
    if (!content) {
      close();
      return;
    }
    results.replaceChildren(document.importNode(content, true));
    highlight(q);
    if (popular) popular.hidden = true;
    setActive(-1);
    panel.scrollTop = 0;
    if (document.activeElement === input || !panel.hidden) open();
  };

  const fetchResults = (q) => {
    if (cache.has(q)) {
      render(q, cache.get(q));
      return;
    }
    if (controller) controller.abort();
    controller = new AbortController();
    if (spinner) spinner.hidden = false;
    const params = new URLSearchParams();
    params.set('q', q);
    params.set('section_id', sectionId);
    params.set('resources[type]', 'product,collection');
    params.set('resources[limit]', '10');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,vendor,product_type,variants.title,tag');
    fetch(suggestUrl + '?' + params.toString(), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        cache.set(q, html);
        render(q, html);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') close();
      })
      .finally(() => {
        if (spinner) spinner.hidden = true;
      });
  };

  const onInput = () => {
    const q = term();
    if (clearBtn) clearBtn.hidden = q.length === 0;
    clearTimeout(timer);
    if (q.length < 2) {
      if (controller) controller.abort();
      if (q.length === 0 && document.activeElement === input) {
        showPopular();
      } else {
        results.replaceChildren();
        if (popular) popular.hidden = true;
        close();
      }
      return;
    }
    timer = setTimeout(() => fetchResults(q), 180);
  };

  input.addEventListener('input', onInput);

  input.addEventListener('focus', () => {
    const q = term();
    if (q.length === 0) {
      showPopular();
    } else if (q.length > 1) {
      if (results.childElementCount) open();
      else fetchResults(q);
    }
  });

  input.addEventListener('keydown', (event) => {
    const list = visibleOptions();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (panel.hidden) {
        onInput();
        return;
      }
      setActive(activeIndex + 1 >= list.length ? 0 : activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!panel.hidden) setActive(activeIndex <= 0 ? list.length - 1 : activeIndex - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = !panel.hidden ? list[activeIndex] : null;
      if (chosen) chosen.click();
      else goToResults(term());
    } else if (event.key === 'Escape') {
      if (!panel.hidden) {
        event.preventDefault();
        close();
      }
    }
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      input.focus();
      onInput();
    });
  }

  /* Le voci non tolgono il cursore dal campo (così il clic arriva prima della chiusura). */
  panel.addEventListener('mousedown', (event) => {
    if (event.target.closest('[role="option"]')) event.preventDefault();
  });

  panel.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-cv-hfind-term]');
    if (!chip) return;
    input.value = chip.dataset.cvHfindTerm || chip.textContent.trim();
    input.focus();
    onInput();
  });

  document.addEventListener('click', (event) => {
    if (!root.contains(event.target)) close();
  });

  input.addEventListener('blur', () => {
    setTimeout(() => {
      if (keepOpen) {
        keepOpen = false;
        scheduleFit();
        return;
      }
      if (!root.contains(document.activeElement)) close();
    }, 150);
  });

  /* Telefono: trascinando la lista con il dito si chiude la tastiera; la tendina resta aperta e si allunga. */
  panel.addEventListener(
    'touchstart',
    (event) => {
      touchStartY = event.touches.length === 1 ? event.touches[0].clientY : null;
    },
    { passive: true }
  );
  panel.addEventListener(
    'touchmove',
    (event) => {
      if (touchStartY === null || document.activeElement !== input) return;
      if (Math.abs(event.touches[0].clientY - touchStartY) < 10) return;
      touchStartY = null;
      keepOpen = true;
      input.blur();
    },
    { passive: true }
  );
  panel.addEventListener(
    'touchend',
    () => {
      touchStartY = null;
    },
    { passive: true }
  );

  /* La tastiera che si apre o si chiude, la rotazione e lo scorrimento della pagina cambiano lo spazio disponibile. */
  if (vv) {
    vv.addEventListener('resize', scheduleFit);
    vv.addEventListener('scroll', scheduleFit);
  }
  window.addEventListener('resize', scheduleFit);
  window.addEventListener('scroll', scheduleFit, { passive: true });

  /* Lente dell'header: invece della ricerca del tema, porta il cliente a questa barra. */
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('.search-bar_dropdown-opener');
      if (!opener || root.offsetParent === null) return;
      event.preventDefault();
      event.stopPropagation();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      input.focus({ preventScroll: true });
    },
    true
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) close();
  });
})();
